import React from 'react';
import { SentenceApi } from '../api/SentenceApi';
import { handleErrorMessage } from '../component/ErrorDialog';
import { pinyinForEditing } from './pinyinForEditing';
import { usePinyinRequests } from './pinyinRequests';

interface CustomInputTextPinyinState {
    text: string;
    pinyin: string;
    pinyinList: string[];
    pinyinError: string | null;
}

interface UseCustomInputTextPinyinListProps {
    history: any;
    location: any;
    setErrorMessage: (msg: string) => void;
}

function isTextPinyinPairVariable(variable: string) {
    return variable.startsWith('<CustomInput') && variable.endsWith('TextPinyinPair/>')
}
function getPairPinyinVariable(variable: string) {
    const pinyinVariable = variable.replace('TextPinyinPair/>', 'TextPinyinPairPinyin/>')
    return pinyinVariable;
}

export function useCustomInputTextPinyinList({
    history,
    location,
    setErrorMessage,
}: UseCustomInputTextPinyinListProps) {

    const [customKeys, setCustomKeys] = React.useState<string[]>([]);
    const [customStateMap, setCustomStateMap] = React.useState<Record<string, CustomInputTextPinyinState>>({});

    const suggestions = usePinyinRequests<string>();
    const validations = usePinyinRequests<string>();
    const customStateMapRef = React.useRef(customStateMap);
    React.useEffect(() => {
        customStateMapRef.current = customStateMap;
    }, [customStateMap]);

    const setCustomInputTextVariableList = React.useCallback((variableList: string[]) => {
        const pairList = variableList.filter(v => isTextPinyinPairVariable(v));

        const newState: Record<string, CustomInputTextPinyinState> = {};
        for (const key of pairList) {
            newState[key] = { text: '', pinyin: '', pinyinList: [], pinyinError: null };
        }

        suggestions.cancelAll();
        validations.cancelAll();
        setCustomKeys(pairList);
        setCustomStateMap(newState);
    }, [suggestions, validations]);

    const handleCustomInputTextChange = React.useCallback((variable: string, value: string) => {
        setCustomStateMap(prev => ({
            ...prev,
            // A reading belongs to its text: changed text has none until its suggestion comes, so none can be saved with it.
            [variable]: { ...prev[variable], text: value, pinyin: '', pinyinList: [] }
        }));

        const isLatest = suggestions.start(variable);
        validations.start(variable);
        SentenceApi.getTextPinyin(value)
            .then((response) => {
                if (!isLatest()) return;
                const pinyinList = response.data;
                setCustomStateMap(prev => ({
                    ...prev,
                    [variable]: {
                        ...prev[variable],
                        pinyin: pinyinList.map(inner => inner.join(' ')).join('\n'),
                        pinyinList: pinyinList.flat(),
                        pinyinError: null
                    }
                }));
            })
            .catch((err) => {
                if (!isLatest()) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setCustomStateMap(prev => ({
                    ...prev,
                    [variable]: {
                        ...prev[variable],
                        pinyinError: 'Unable to fetch pinyin'
                    }
                }));
            });
    }, [suggestions, validations, setErrorMessage, history, location]);

    const handleCustomInputTextPinyinChange = React.useCallback((variable: string, value: string) => {
        const list = value.trim().split(/\s+/).filter(Boolean);
        suggestions.start(variable);
        const isLatest = validations.start(variable);
        setCustomStateMap(prev => ({
            ...prev,
            [variable]: {
                ...prev[variable],
                pinyin: value,
                pinyinList: list
            }
        }));

        SentenceApi.isTextPinyinValid(customStateMapRef.current[variable]?.text ?? '', list)
            .then((response) => {
                if (!isLatest()) return;
                const isValid = response.data;
                setCustomStateMap(prev => ({
                    ...prev,
                    [variable]: {
                        ...prev[variable],
                        pinyinError: isValid ? null : 'Pinyin invalid or incorrect length'
                    }
                }));
            })
            .catch((err) => {
                if (!isLatest()) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setCustomStateMap(prev => ({
                    ...prev,
                    [variable]: {
                        ...prev[variable],
                        pinyinError: 'Unable to validate pinyin'
                    }
                }));
            });
    }, [suggestions, validations, setErrorMessage, history, location]);

    const setCustomInputTextPinyinField = React.useCallback((variable: string, text: string, savedPinyinList: string[]) => {
        setCustomStateMap(prev => ({
            ...prev,
            [variable]: {
                text,
                pinyin: '',
                pinyinList: [],
                pinyinError: null
            }
        }));

        if (!text.trim()) {
            return;
        }

        const isLatest = suggestions.start(variable);
        validations.start(variable);
        pinyinForEditing(text, savedPinyinList)
            .then((numericPinyinList) => {
                if (!isLatest()) return;
                setCustomStateMap(prev => ({
                    ...prev,
                    [variable]: {
                        text,
                        pinyin: numericPinyinList.join(' '),
                        pinyinList: numericPinyinList,
                        pinyinError: null
                    }
                }));
            })
            .catch((err) => {
                if (!isLatest()) return;
                handleErrorMessage(err, setErrorMessage, history, location);
                setCustomStateMap(prev => ({
                    ...prev,
                    [variable]: {
                        text,
                        pinyin: '',
                        pinyinList: [],
                        pinyinError: 'Unable to fetch pinyin'
                    }
                }));
            });
    }, [suggestions, validations, setErrorMessage, history, location]);

    const clearCustomInputTextPinyinField = React.useCallback((variable: string) => {
        suggestions.start(variable);
        validations.start(variable);
        setCustomStateMap(prev => ({
            ...prev,
            [variable]: {
                text: '',
                pinyin: '',
                pinyinList: [],
                pinyinError: null
            }
        }));
    }, [suggestions, validations]);

    const clearCustomInputTextPinyinAllField = React.useCallback(() => {
        suggestions.cancelAll();
        validations.cancelAll();
        setCustomStateMap(prev =>
            Object.fromEntries(
                Object.keys(prev).map(k => [k, { text: '', pinyin: '', pinyinList: [], pinyinError: null }])
            )
        );
    }, [suggestions, validations]);

    const isCustomInputTextVariable = React.useCallback((variable: string) => (variable in customStateMapRef.current), []);

    const customInputTextMap = React.useMemo(() => {
        const result: Record<string, string> = {};
        for (let i = 0; i < customKeys.length; i++) {
            const key = customKeys[i];
            if (customStateMap[key] && typeof customStateMap[key].text === 'string') {
                result[key] = customStateMap[key].text;
            }
        }
        return result;
    }, [customKeys, customStateMap]);

    const customInputTextPinyinMap = React.useMemo(() => {
        const result: Record<string, string> = {};
        for (let i = 0; i < customKeys.length; i++) {
            const key = customKeys[i];
            if (customStateMap[key] && typeof customStateMap[key].pinyin === 'string') {
                result[key] = customStateMap[key].pinyin;
            }
        }
        return result;
    }, [customKeys, customStateMap]);

    const customInputTextPinyinListMap = React.useMemo(() => {
        const result: Record<string, string[]> = {};
        for (let i = 0; i < customKeys.length; i++) {
            const key = customKeys[i];
            if (customStateMap[key] && Array.isArray(customStateMap[key].pinyinList)) {
                const pinyinVariable = getPairPinyinVariable(key)
                result[pinyinVariable] = customStateMap[key].pinyinList;
            }
        }
        return result;
    }, [customKeys, customStateMap]);

    const customInputTextPinyinErrorMap = React.useMemo(() => {
        const result: Record<string, string | null> = {};
        for (let i = 0; i < customKeys.length; i++) {
            const key = customKeys[i];
            if (customStateMap[key]) {
                result[key] = customStateMap[key].pinyinError;
            }
        }
        return result;
    }, [customKeys, customStateMap]);

    return {
        customKeys,
        customInputTextMap,
        customInputTextPinyinMap, // warning key variable name, does NOT Pinyin/> suffix
        customInputTextPinyinListMap, // warning key variable name, has Pinyin/> suffix
        customInputTextPinyinErrorMap,
        isCustomInputTextVariable,
        handleCustomInputTextChange,
        handleCustomInputTextPinyinChange,
        setCustomInputTextVariableList,
        setCustomInputTextPinyinField,
        clearCustomInputTextPinyinField,
        clearCustomInputTextPinyinAllField
    };
}
